/**
 * Utility functions for handling project categorization and multi-activity projects
 */

import { activities } from '../../../db';

interface ProjectWithActivityId {
  id: number;
  title: string;
  activityId: number | number[] | null; // Allow null for private projects
  startDate: string;
  endDate?: string | null;
  description: string;
  [key: string]: any;
}

interface CategorizedProject extends ProjectWithActivityId {
  isContinued: boolean;
  previousActivityId?: number;
  previousActivityTitle?: string;
  nextActivityId?: number;
  nextActivityTitle?: string;
}

/**
 * Checks if a project spans multiple activities
 */
export function isMultiActivityProject(project: ProjectWithActivityId): boolean {
  return Array.isArray(project.activityId);
}

/**
 * Gets all activity IDs for a project (handles both single and multi-activity projects)
 * Returns empty array for private projects (activityId = null)
 */
export function getProjectActivityIds(project: ProjectWithActivityId): number[] {
  if (project.activityId === null) {
    return []; // Private projects have no associated activities
  }
  return Array.isArray(project.activityId) ? project.activityId : [project.activityId];
}

/**
 * Checks if a project is relevant to a specific activity
 */
export function isProjectRelevantToActivity(project: ProjectWithActivityId, activityId: number): boolean {
  const projectActivityIds = getProjectActivityIds(project);
  return projectActivityIds.includes(activityId);
}

/**
 * Determines if a project is a continuation for the given activity
 * A project is considered continued if:
 * 1. It spans multiple activities AND
 * 2. The given activity is not the first activity in the project's timeline
 */
export function isProjectContinuedForActivity(project: ProjectWithActivityId, currentActivityId: number): boolean {
  if (!isMultiActivityProject(project)) {
    return false;
  }
  
  const projectActivityIds = getProjectActivityIds(project);
  const activityStartDates = projectActivityIds.map(id => {
    const activity = activities.find((a: any) => a.id === id);
    return { id, startDate: activity?.startDate || '' };
  }).sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  
  const firstActivityId = activityStartDates[0]?.id;
  return projectActivityIds.includes(currentActivityId) && currentActivityId !== firstActivityId;
}

/**
 * Gets the previous activity ID for a continued project
 */
export function getPreviousActivityId(project: ProjectWithActivityId, currentActivityId: number): number | undefined {
  if (!isMultiActivityProject(project)) {
    return undefined;
  }
  
  const projectActivityIds = getProjectActivityIds(project);
  const activityStartDates = projectActivityIds.map(id => {
    const activity = activities.find((a: any) => a.id === id);
    return { id, startDate: activity?.startDate || '' };
  }).sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  
  const currentIndex = activityStartDates.findIndex(a => a.id === currentActivityId);
  return currentIndex > 0 ? activityStartDates[currentIndex - 1].id : undefined;
}

/**
 * Gets the next activity ID for a continued project
 */
export function getNextActivityId(project: ProjectWithActivityId, currentActivityId: number): number | undefined {
  if (!isMultiActivityProject(project)) {
    return undefined;
  }
  
  const projectActivityIds = getProjectActivityIds(project);
  const activityStartDates = projectActivityIds.map(id => {
    const activity = activities.find((a: any) => a.id === id);
    return { id, startDate: activity?.startDate || '' };
  }).sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  
  const currentIndex = activityStartDates.findIndex(a => a.id === currentActivityId);
  return currentIndex < activityStartDates.length - 1 ? activityStartDates[currentIndex + 1].id : undefined;
}

/**
 * Gets the activity title by ID
 */
export function getActivityTitle(activityId: number): string {
  const activity = activities.find((a: any) => a.id === activityId);
  return activity?.title || 'Unknown Activity';
}

/**
 * Categorizes projects for a specific activity into new and continued projects
 */
export function categorizeProjectsForActivity(
  projects: ProjectWithActivityId[], 
  activityId: number
): { newProjects: CategorizedProject[]; continuedProjects: CategorizedProject[] } {
  const relevantProjects = projects.filter(project => 
    isProjectRelevantToActivity(project, activityId)
  );
  
  const newProjects: CategorizedProject[] = [];
  const continuedProjects: CategorizedProject[] = [];
  
  relevantProjects.forEach(project => {
    const isContinued = isProjectContinuedForActivity(project, activityId);
    const previousActivityId = isContinued ? getPreviousActivityId(project, activityId) : undefined;
    const nextActivityId = getNextActivityId(project, activityId);
    
    const categorizedProject: CategorizedProject = {
      ...project,
      isContinued,
      previousActivityId,
      previousActivityTitle: previousActivityId ? getActivityTitle(previousActivityId) : undefined,
      nextActivityId,
      nextActivityTitle: nextActivityId ? getActivityTitle(nextActivityId) : undefined
    };
    
    if (isContinued) {
      continuedProjects.push(categorizedProject);
    } else {
      newProjects.push(categorizedProject);
    }
  });
  
  return { newProjects, continuedProjects };
}
